/**
 * RS / Product Rails — auto-scrolling product lines.
 *
 * Each rail holds one server-rendered group of cards; the group is cloned
 * (inert, aria-hidden) until the track is wide enough to loop seamlessly.
 * A single rAF loop advances the offsets — it only runs while the section is
 * in the viewport, and all layout reads (group width, viewport) are cached in
 * #measure() so the tick itself never touches layout.
 *
 * Interactions: hover pauses (pointer devices), drag/swipe scrubs the line
 * with the shared hero physics (1:1 under the finger, decaying fling on
 * release, a new touch catches the glide), horizontal trackpad wheel scrolls
 * it. A drag suppresses the click so cards don't navigate at the end of a
 * swipe. Autoplay suspends while a gesture or its fling is alive and resumes
 * from the current offset — no jump.
 */
import { createDragPhysics } from 'rs-drag-physics';

class RsProductRails extends HTMLElement {
  #raf = null;
  #lastTime = 0;
  #observer = null;
  #autoplay = true;

  connectedCallback() {
    this.speed = parseFloat(this.dataset.speed) || 30;

    this.rows = Array.from(this.querySelectorAll('[data-rs-rail]')).map((rail) => ({
      rail,
      track: rail.querySelector('[data-rs-track]'),
      group: rail.querySelector('[data-rs-group]'),
      dir: rail.dataset.direction === 'right' ? -1 : 1,
      off: 0,
      cycle: 0,
      hover: false,
    }));

    for (const row of this.rows) this.#wireRow(row);
    this.#measure();

    // iOS toolbar show/hide fires height-only resizes while the page scrolls:
    // re-cloning mid-animation makes the rails visibly hitch
    this.lastMeasuredWidth = window.innerWidth;
    this.onResize = () => {
      if (window.innerWidth === this.lastMeasuredWidth) return;
      this.lastMeasuredWidth = window.innerWidth;
      this.#measure();
    };
    window.addEventListener('resize', this.onResize);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#start();
        } else {
          this.#stop();
        }
      }
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
    window.removeEventListener('resize', this.onResize);
    for (const row of this.rows) row.physics?.destroy();
  }

  /* Cached layout pass: clone the group until the track can loop, then store
     the cycle width. The rAF tick reads only these cached values. */
  #measure() {
    const viewport = window.innerWidth;
    for (const row of this.rows) {
      if (!row.group || !row.track) continue;
      for (const clone of row.track.querySelectorAll('[data-rs-clone]')) clone.remove();

      const groupWidth = row.group.getBoundingClientRect().width;
      if (groupWidth === 0) continue;
      const gap = parseFloat(getComputedStyle(row.track).columnGap) || 0;

      const copies = Math.max(2, Math.ceil((viewport * 2) / (groupWidth + gap)));
      for (let i = 1; i < copies; i++) {
        const clone = row.group.cloneNode(true);
        clone.setAttribute('data-rs-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        // NOT inert: clones fill most of the visible track and must stay
        // clickable; out of the tab order via tabindex only
        for (const focusable of clone.querySelectorAll('a, button')) focusable.tabIndex = -1;
        row.track.appendChild(clone);
      }
      row.cycle = groupWidth + gap;
    }
  }

  #wireRow(row) {
    const { rail } = row;

    // Hover pause only from a real mouse: touch taps synthesize mouseenter
    // without ever leaving, which froze the rails on mobile
    rail.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') row.hover = true;
    });
    rail.addEventListener('pointerleave', () => (row.hover = false));

    // Shared hero physics: 1:1 drag, decaying fling, catch on touch
    row.physics = createDragPhysics(rail, {
      getOffset: () => row.off,
      onDragStart: () => {
        row.startOff = row.off;
        rail.classList.add('is-dragging');
      },
      onMove: (dx) => {
        row.off = row.startOff - dx;
      },
      onDragEnd: () => rail.classList.remove('is-dragging'),
      onFling: (delta) => {
        row.off += delta;
      },
    });

    // A completed drag must not navigate to the product under the pointer
    rail.addEventListener(
      'click',
      (event) => {
        if (!row.physics.moved) return;
        row.physics.resetMoved();
        event.preventDefault();
        event.stopPropagation();
      },
      { capture: true }
    );

    // Two-finger swipe on trackpad / Magic Mouse
    rail.addEventListener(
      'wheel',
      (event) => {
        const dx = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : 0;
        if (dx === 0) return;
        event.preventDefault();
        row.physics.stopFling();
        row.off += dx;
      },
      { passive: false }
    );
  }

  #start() {
    if (this.#raf) return;
    this.#lastTime = performance.now();
    this.#raf = requestAnimationFrame(this.#tick);
  }

  #stop() {
    cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }

  #tick = (time) => {
    const dt = Math.min((time - this.#lastTime) / 1000, 0.05);
    this.#lastTime = time;

    for (const row of this.rows) {
      if (!row.track || row.cycle <= 0) continue;
      if (this.#autoplay && !row.hover && !row.physics.active) {
        row.off += this.speed * dt * row.dir;
      }
      row.off = ((row.off % row.cycle) + row.cycle) % row.cycle;
      row.track.style.transform = `translate3d(${-row.off}px, 0, 0)`;
    }

    this.#raf = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('rs-product-rails')) {
  customElements.define('rs-product-rails', RsProductRails);
}
